/**
 * Design Editor page — the main editing environment.
 * Contains the split 2D/3D viewport, toolbar, and properties panel.
 * See wireframe: section 5 Design Editor -- Main Layout
 */

import type { RouteParams } from '@/router/router';

/** Maps HTML data-tool attributes to ToolId values. */
const TOOL_BUTTON_MAP: Record<string, import('@/interaction/tools/Tool').ToolId> = {
  select: 'select',
  rect: 'rectangle',
  poly: 'polygon',
  measure: 'measure',
  chimney: 'chimney',
  skylight: 'skylight',
  dormer: 'dormer',
};

/** Reverse lookup: ToolId → data-tool attribute value. */
const TOOL_ID_TO_DATA: Record<string, string> = {};
for (const [attr, id] of Object.entries(TOOL_BUTTON_MAP)) {
  TOOL_ID_TO_DATA[id] = attr;
}

/** Monotonically increasing ID to disambiguate editor instances for the same project. */
let editorInstanceId = 0;

export function EditorPage(params: RouteParams): HTMLElement {
  const projectId = params['projectId'] ?? '';

  const page = document.createElement('div');
  page.className = 'editor-page';
  page.dataset['projectId'] = projectId;
  page.style.cssText = 'height:100%;display:flex;flex-direction:column;overflow:hidden;';

  page.innerHTML = `
    <!-- Top Bar -->
    <header class="editor-topbar" role="banner">
      <a class="topbar-logo" href="#/dashboard" title="Back to Projects">RoofmAIte</a>
      <button class="btn btn-ghost editor-project-name" id="project-name-btn" aria-label="Rename project">
        Loading…
      </button>
      <div class="editor-topbar-center">
        <button class="btn btn-ghost btn-icon" id="undo-btn" title="Undo (Ctrl+Z)" disabled aria-label="Undo">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 14L4 9l5-5" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 9h11a5 5 0 010 10h-1" />
          </svg>
        </button>
        <button class="btn btn-ghost btn-icon" id="redo-btn" title="Redo (Ctrl+Y)" disabled aria-label="Redo">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 14l5-5-5-5" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M20 9H9a5 5 0 000 10h1" />
          </svg>
        </button>
        <button class="btn btn-secondary btn-sm" id="save-btn" title="Save (Ctrl+S)">
          Save
        </button>
      </div>
      <div class="editor-topbar-right">
        <select class="mode-select" id="mode-select" aria-label="Design mode">
          <option value="simple">Simple Mode</option>
          <option value="advanced" selected>Advanced Mode</option>
        </select>
      </div>
    </header>

    <!-- Toolbar -->
    <div class="editor-toolbar" role="toolbar" aria-label="Drawing tools">
      <div class="toolbar-tools" id="toolbar-tools">
        <button class="btn btn-ghost btn-icon active" data-tool="select" title="Select (V)" aria-pressed="true" aria-label="Select tool">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
          </svg>
        </button>
        <button class="btn btn-ghost btn-icon" data-tool="rect" title="Draw Rectangle (R)" aria-pressed="false" aria-label="Draw rectangle">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" />
          </svg>
        </button>
        <button class="btn btn-ghost btn-icon advanced-only" data-tool="poly" title="Draw Polygon (P)" aria-pressed="false" aria-label="Draw polygon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 2l8 6.5-3 9H7L4 8.5z" />
          </svg>
        </button>
        <button class="btn btn-ghost btn-icon advanced-only" data-tool="measure" title="Measure (M)" aria-pressed="false" aria-label="Measure tool">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 6l18 12M6 3v3m4-2v2m4-1v3m4-2v2" />
          </svg>
        </button>
        <div class="toolbar-separator"></div>
        <button class="btn btn-ghost btn-icon" data-tool="chimney" title="Place Chimney (C)" aria-pressed="false" aria-label="Place chimney">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <rect x="7" y="4" width="6" height="10" rx="1" />
            <path stroke-linecap="round" d="M7 20h6M4 20h16" />
          </svg>
        </button>
        <button class="btn btn-ghost btn-icon" data-tool="skylight" title="Place Skylight (S)" aria-pressed="false" aria-label="Place skylight">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <rect x="4" y="4" width="16" height="16" rx="2" />
            <path stroke-linecap="round" d="M4 9h16M4 15h16M9 4v16M15 4v16" />
          </svg>
        </button>
        <button class="btn btn-ghost btn-icon advanced-only" data-tool="dormer" title="Place Dormer (D)" aria-pressed="false" aria-label="Place dormer">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 20l9-16 9 16H3z" />
            <rect x="9" y="12" width="6" height="8" />
          </svg>
        </button>
      </div>
      <div class="toolbar-actions">
        <button class="btn btn-secondary btn-sm" id="export-btn" title="Export">Export</button>
        <button class="btn btn-secondary btn-sm" id="materials-btn" title="Material Estimate">Materials</button>
        <button class="btn btn-ghost btn-sm" id="history-btn" title="Version History">History</button>
      </div>
    </div>

    <!-- Main Editor Area -->
    <div class="editor-main" id="editor-main">
      <!-- Viewport container — SceneManager mounts here -->
      <div class="editor-viewport-container" id="viewport-container" role="region" aria-label="Design viewport">
        <!-- Pane labels (on top of the Three.js canvas, pointer-events: none) -->
        <div class="pane-label pane-label-left" aria-hidden="true">2D Plan</div>
        <div class="pane-label pane-label-right" aria-hidden="true">3D View</div>
      </div>

      <!-- Properties Panel (PropertiesPanel component replaces inner content via buildShell) -->
      <aside class="properties-panel" id="properties-panel" role="complementary" aria-label="Properties">
        <div class="properties-loading">
          <div class="spinner"></div>
        </div>
      </aside>
    </div>

    <!-- Status Bar -->
    <footer class="status-bar" role="status" aria-live="polite">
      <span id="status-tool">Ready</span>
      <span class="status-separator">|</span>
      <span id="status-grid">Grid: 1m</span>
      <span class="status-separator">|</span>
      <span id="status-units" class="status-clickable" title="Click to toggle units">Units: Metric</span>
      <span class="status-separator">|</span>
      <span id="status-zoom">Zoom: 100%</span>
    </footer>
  `;

  // Lazy-load the actual Three.js scene — deferred to avoid blocking mount
  const instanceId = ++editorInstanceId;
  requestAnimationFrame(() => {
    initEditor(page, projectId, instanceId);
  });

  return page;
}

async function initEditor(page: HTMLElement, projectId: string, instanceId: number): Promise<void> {
  // ── Dynamic imports (keep initial bundle small) ─────────────────────────
  const [
    { SceneManager },
    { initGeometryEngine },
    { RaycasterManager },
    tools,
    { PropertiesPanel },
    { eventBus },
    designState,
    commandStack,
    { AddFootprintCmd, AddElementCmd, MoveElementCmd, ModifyElementCmd },
    { projectsApi },
    { ElementMeshBuilder, computeFacePlane },
    THREE,
    keyboardShortcuts,
    { CSGOperations },
    unitConv,
    { findContainingFace },
  ] = await Promise.all([
    import('@/renderer/SceneManager'),
    import('@/geometry/RoofGeometryComputer'),
    import('@/interaction/RaycasterManager'),
    import('@/interaction/tools'),
    import('@/ui/components/PropertiesPanel'),
    import('@/state/EventBus'),
    import('@/state/DesignState'),
    import('@/state/CommandStack'),
    import('@/state/commands/DesignCommands'),
    import('@/api/projectsApi'),
    import('@/renderer/ElementMeshBuilder'),
    import('three'),
    import('@/ui/components/KeyboardShortcuts'),
    import('@/interaction/CSGOperations'),
    import('@/utils/unitConverter'),
    import('@/interaction/ElementValidator'),
  ]);

  // ── Initialize WASM geometry engine ─────────────────────────────────────
  await initGeometryEngine();

  // Guard: if a newer editor instance was created while we were doing async imports,
  // or if the page was removed from the DOM, bail out.
  if (instanceId !== editorInstanceId || !document.body.contains(page)) return;

  // ── Mount SceneManager ──────────────────────────────────────────────────
  const viewportContainer = page.querySelector<HTMLElement>('#viewport-container')!;
  const sceneManager = new SceneManager(viewportContainer);
  // ── Element mesh builder & tracking ─────────────────────────────────────
  const elementMeshBuilder = new ElementMeshBuilder(sceneManager.materials);
  const elementMeshGroups = new Map<string, import('@/renderer/ElementMeshBuilder').ElementMeshGroup>();
  const csgOps = new CSGOperations();

  // ── Raycaster ───────────────────────────────────────────────────────────
  const raycaster = new RaycasterManager();
  raycaster.setGroundMeshes([sceneManager.groundPlane.object]);
  // Debug: expose raycaster and scene for console inspection
  (window as any).__raycaster = raycaster;
  (window as any).__scene = sceneManager;

  // ── Preview group (for tool ghost meshes) ───────────────────────────────
  const previewGroup = new THREE.Group();
  previewGroup.name = 'tool-preview';
  sceneManager.add(previewGroup);

  // ── Tool context ────────────────────────────────────────────────────────
  let advancedMode = true;
  const toolOutputQueue: import('@/interaction/tools/Tool').ToolOutputEvent[] = [];

  // Tracks which camera is active based on the pane that received the last pointerdown.
  let activeCamera: import('three').Camera = sceneManager.perspectiveView.camera;

  const toolContext: import('@/interaction/tools/Tool').ToolContext = {
    scene: sceneManager.scene,
    emit(event) {
      toolOutputQueue.push(event);
      // Process synchronously after tool handler returns
      queueMicrotask(() => processToolOutput());
    },
    setStatus(message) {
      const statusEl = page.querySelector<HTMLElement>('#status-tool');
      if (statusEl) statusEl.textContent = message;
    },
    showPreview(object) {
      previewGroup.add(object);
      sceneManager.markDirty();
    },
    clearPreview() {
      while (previewGroup.children.length > 0) {
        previewGroup.remove(previewGroup.children[0]);
      }
      sceneManager.markDirty();
    },
    get advancedMode() {
      return advancedMode;
    },
    markDirty() {
      sceneManager.markDirty();
    },
    get camera() {
      return activeCamera;
    },
  };

  // ── ToolManager ─────────────────────────────────────────────────────────
  const toolManager = new tools.ToolManager(document, toolContext);
  toolManager.register(new tools.SelectTool());
  toolManager.register(new tools.RectangleTool());
  toolManager.register(new tools.PolygonTool());
  toolManager.register(new tools.MeasurementTool());
  toolManager.register(new tools.ChimneyTool());
  toolManager.register(new tools.SkylightTool());
  toolManager.register(new tools.DormerTool());
  toolManager.setAdvancedMode(true);

  // ── Pointer event routing ───────────────────────────────────────────────
  // Canvas has pointer-events:none, so events arrive at the panes behind it.
  // Left-click is intercepted in capture phase for tools; middle/right pass to controls.
  //
  // interactionPane tracks which pane "owns" the current pointer interaction.
  // While a left-button drag is in progress, events from the *other* pane are
  // ignored — this prevents cross-pane interference that could reset tool state.
  const planPane = sceneManager.splitter.planPane;
  const perspPane = sceneManager.splitter.perspectivePane;
  let interactionPane: HTMLElement | null = null;

  function setupPointerRouting(pane: HTMLElement, isPlan: boolean): void {
    const getCamera = () => isPlan ? sceneManager.planView.camera : sceneManager.perspectiveView.camera;

    const doRaycast = (event: PointerEvent | MouseEvent) => {
      return raycaster.castFromPointer(event, getCamera(), pane);
    };

    // Capture phase: intercept left-button events for tools
    pane.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return; // only left button
      if (interactionPane && interactionPane !== pane) return; // other pane owns interaction
      interactionPane = pane;
      activeCamera = getCamera();
      e.stopImmediatePropagation(); // prevent controls from orbiting/panning
      const hit = doRaycast(e);
      toolManager.onPointerDown(e, hit);
    }, { capture: true });

    pane.addEventListener('pointermove', (e) => {
      if (interactionPane && interactionPane !== pane) return;
      // Move is always routed to tools (for hover/preview), but don't stop propagation
      // so controls can still respond to middle/right drags
      const hit = doRaycast(e);
      toolManager.onPointerMove(e, hit);
    });

    pane.addEventListener('pointerup', (e) => {
      if (e.button !== 0) return;
      if (interactionPane && interactionPane !== pane) return;
      interactionPane = null;
      const hit = doRaycast(e);
      toolManager.onPointerUp(e, hit);
    });

    pane.addEventListener('dblclick', (e) => {
      const hit = doRaycast(e);
      toolManager.onDoubleClick(e, hit);
    });

    pane.addEventListener('contextmenu', (e) => {
      const hit = doRaycast(e);
      if (toolManager.onContextMenu(e, hit)) {
        e.preventDefault();
      }
    });
  }

  setupPointerRouting(planPane, true);
  setupPointerRouting(perspPane, false);

  // ── Toolbar button wiring ───────────────────────────────────────────────
  const toolBtns = page.querySelectorAll<HTMLButtonElement>('[data-tool]');

  function updateToolButtonStates(activeDataTool: string): void {
    toolBtns.forEach(btn => {
      const isActive = btn.dataset['tool'] === activeDataTool;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    });
  }

  toolBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const toolAttr = btn.dataset['tool'] ?? 'select';
      const toolId = TOOL_BUTTON_MAP[toolAttr];
      if (toolId) toolManager.activate(toolId);
    });
  });

  // ── ToolManager events ──────────────────────────────────────────────────
  toolManager.on((event) => {
    if (event.type === 'toolChanged') {
      const dataAttr = TOOL_ID_TO_DATA[event.toolId] ?? 'select';
      updateToolButtonStates(dataAttr);
    }
    if (event.type === 'statusChanged') {
      const statusEl = page.querySelector<HTMLElement>('#status-tool');
      if (statusEl) statusEl.textContent = event.message;
    }
  });

  // Activate default tool after listener is wired so status bar updates
  toolManager.activate('select');

  // ── Editor keyboard shortcuts (Delete/Backspace for element removal) ────
  keyboardShortcuts.bindEditorShortcuts();

  // ── Tool output processing ──────────────────────────────────────────────
  let buildingCounter = 0;

  function processToolOutput(): void {
    while (toolOutputQueue.length > 0) {
      const event = toolOutputQueue.shift()!;

      switch (event.type) {
        case 'footprint-added': {
          buildingCounter++;
          const building: import('@/types').BuildingDesign = {
            id: crypto.randomUUID(),
            name: `Building ${buildingCounter}`,
            parameters: {
              type: 'gable',
              footprint: {
                vertices: event.vertices.map(v => ({ x: v.x, z: v.z })),
                wallPlateHeight: event.wallPlateHeight,
              },
              pitch: { risePer12: 6 },
              overhangEaveMm: 305,
              overhangRakeMm: 305,
              fasciaHeightMm: 150,
            },
            elements: [],
            materialSelection: {
              shingleType: 'three-tab',
              underlaymentType: 'felt-30',
              valleyType: 'closed-cut',
            },
          };
          commandStack.executeCommand(new AddFootprintCmd(building));
          updateUndoRedoState();
          break;
        }

        case 'element-placed': {
          const buildings = designState.getBuildings();
          if (buildings.length === 0) break;
          // Find the building that owns the face
          let targetBuilding = buildings[0];
          for (const b of buildings) {
            if (b.geometry?.faces.some(f => f.id === event.faceId)) {
              targetBuilding = b;
              break;
            }
          }
          const element: import('@/types').RoofElement = {
            id: crypto.randomUUID(),
            type: event.elementType,
            faceId: event.faceId,
            positionPlan: { x: event.positionX, z: event.positionZ },
            widthMm: event.widthMm,
            depthMm: event.depthMm,
          };
          commandStack.executeCommand(new AddElementCmd(targetBuilding.id, element));
          updateUndoRedoState();
          break;
        }

        case 'element-moved': {
          const buildings = designState.getBuildings();
          // Find the building that owns this element
          for (const b of buildings) {
            const el = b.elements.find(e => e.id === event.elementId);
            if (el) {
              const newPos = { x: event.newPositionX, z: event.newPositionZ };
              // If faceId changed, use ModifyElementCmd to update both position and face
              if (event.faceId && event.faceId !== el.faceId) {
                commandStack.executeCommand(new ModifyElementCmd(
                  b.id, el.id,
                  { positionPlan: { ...el.positionPlan }, faceId: el.faceId },
                  { positionPlan: newPos, faceId: event.faceId },
                ));
              } else {
                const oldPos = { ...el.positionPlan };
                commandStack.executeCommand(new MoveElementCmd(b.id, el.id, oldPos, newPos));
              }
              updateUndoRedoState();
              break;
            }
          }
          break;
        }

        case 'element-resized': {
          const buildings = designState.getBuildings();
          for (const b of buildings) {
            const el = b.elements.find(e => e.id === event.elementId);
            if (el) {
              const oldValues: Record<string, unknown> = { widthMm: el.widthMm, depthMm: el.depthMm };
              const newValues: Record<string, unknown> = { widthMm: event.newWidthMm, depthMm: event.newDepthMm };
              if (event.newHeightMm != null) {
                if (el.type === 'chimney') {
                  oldValues['chimneyHeightAboveRoofMm'] = el.chimneyHeightAboveRoofMm ?? 900;
                  newValues['chimneyHeightAboveRoofMm'] = event.newHeightMm;
                } else if (el.type === 'dormer') {
                  oldValues['dormerWallHeightMm'] = el.dormerWallHeightMm;
                  newValues['dormerWallHeightMm'] = event.newHeightMm;
                }
              }
              commandStack.executeCommand(new ModifyElementCmd(
                b.id, el.id, oldValues, newValues,
              ));
              updateUndoRedoState();
              break;
            }
          }
          break;
        }

        case 'selection-changed': {
          // Deselect all element groups first
          for (const [, emg] of elementMeshGroups) {
            elementMeshBuilder.setSelected(emg.group, false);
          }
          designState.setSelection(event.selectedIds);
          // Apply selection highlight to newly selected items
          for (const id of event.selectedIds) {
            const emg = elementMeshGroups.get(id);
            if (emg) {
              elementMeshBuilder.setSelected(emg.group, true);
            }
          }
          // Update plan overlay labels for the new selection
          updatePlanOverlayLabels();
          sceneManager.markDirty();
          break;
        }

        case 'element-resize-preview': {
          // Live dimension update during resize drag — update the plan overlay label
          const us = designState.getDesign()?.unitSystem ?? 'metric';
          const text = ElementMeshBuilder.formatDimensions(event.widthMm, event.depthMm, us, event.heightMm);
          const lbl = elementPlanLabels.get(event.elementId);
          if (lbl) lbl.textContent = text;
          break;
        }

        case 'measurement-added': {
          // Measurements are visual only — no command needed
          break;
        }
      }
    }
  }

  // ── Undo/Redo ───────────────────────────────────────────────────────────
  const undoBtn = page.querySelector<HTMLButtonElement>('#undo-btn')!;
  const redoBtn = page.querySelector<HTMLButtonElement>('#redo-btn')!;

  function updateUndoRedoState(): void {
    undoBtn.disabled = !commandStack.canUndo();
    redoBtn.disabled = !commandStack.canRedo();
    undoBtn.title = commandStack.undoDescription()
      ? `Undo: ${commandStack.undoDescription()} (Ctrl+Z)`
      : 'Undo (Ctrl+Z)';
    redoBtn.title = commandStack.redoDescription()
      ? `Redo: ${commandStack.redoDescription()} (Ctrl+Y)`
      : 'Redo (Ctrl+Y)';
  }

  undoBtn.addEventListener('click', () => {
    commandStack.undo();
    updateUndoRedoState();
  });
  redoBtn.addEventListener('click', () => {
    commandStack.redo();
    updateUndoRedoState();
  });

  commandStack.bindKeyboardShortcuts();

  // ── Save ────────────────────────────────────────────────────────────────
  const saveBtn = page.querySelector<HTMLButtonElement>('#save-btn')!;

  async function handleSave(): Promise<void> {
    if (!designState.isDirty()) return;
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving…';
    try {
      const data = designState.serializeDesign();
      const result = await projectsApi.saveVersion(projectId, {
        designData: data,
        description: 'Auto-save',
      });
      designState.markSaved(result.versionNumber);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save';
    }
  }

  saveBtn.addEventListener('click', handleSave);

  // Ctrl+S shortcut
  const saveKeyHandler = (e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
  };
  document.addEventListener('keydown', saveKeyHandler);

  // ── Properties Panel ────────────────────────────────────────────────────
  const propsAside = page.querySelector<HTMLElement>('#properties-panel')!;
  const propertiesPanel = new PropertiesPanel(propsAside, {
    advancedMode,
    getElementHeightMm: (elementId: string) => {
      const emg = elementMeshGroups.get(elementId);
      return emg ? (emg.group.userData['heightMm'] as number | undefined) : undefined;
    },
  });

  // ── Mode toggle ─────────────────────────────────────────────────────────
  const modeSelect = page.querySelector<HTMLSelectElement>('#mode-select')!;
  const applyMode = (mode: string) => {
    advancedMode = mode === 'advanced';
    toolManager.setAdvancedMode(advancedMode);
    if (mode === 'simple') {
      page.classList.add('simple-mode');
    } else {
      page.classList.remove('simple-mode');
    }
    propertiesPanel.advancedMode = advancedMode;
  };
  modeSelect.addEventListener('change', () => applyMode(modeSelect.value));
  applyMode(modeSelect.value);

  // ── Unit system toggle ─────────────────────────────────────────────────
  const statusUnitsEl = page.querySelector<HTMLElement>('#status-units');
  const statusGridEl = page.querySelector<HTMLElement>('#status-grid');

  if (statusUnitsEl) {
    statusUnitsEl.addEventListener('click', () => {
      const design = designState.getDesign();
      if (!design) return;
      const newUnit = design.unitSystem === 'metric' ? 'imperial' : 'metric';
      designState.setUnitSystem(newUnit);
    });
  }

  // ── Scene refresh helpers ───────────────────────────────────────────────

  /** Dispose all element meshes and rebuild from current design state. */
  function refreshElementMeshes(): void {
    // Dispose existing element meshes
    for (const [, emg] of elementMeshGroups) {
      sceneManager.remove(emg.group);
      emg.dispose();
    }
    elementMeshGroups.clear();

    const buildings = designState.getBuildings();
    for (const building of buildings) {
      if (!building.geometry) continue;
      for (const element of building.elements) {
        // Find the face this element sits on — use position-based lookup as fallback
        const slopeFaces = building.geometry.faces.filter(f => f.faceType === 'slope');
        let face = element.faceId
          ? slopeFaces.find(f => f.id === element.faceId)
          : undefined;
        if (!face) {
          face = findContainingFace(element.positionPlan, slopeFaces);
        }
        if (!face) continue;

        try {
          const emg = elementMeshBuilder.buildElement(element, face);
          elementMeshGroups.set(element.id, emg);
          sceneManager.add(emg.group);
        } catch (err) {
          console.warn(`Failed to build mesh for element ${element.id}:`, err);
        }
      }
    }

    // Update raycaster element targets and collect handle meshes
    const elementMeshes: InstanceType<typeof THREE.Mesh>[] = [];
    const handleMeshes: InstanceType<typeof THREE.Mesh>[] = [];
    for (const [, emg] of elementMeshGroups) {
      emg.group.traverse((child) => {
        if ('isMesh' in child && child.isMesh) {
          // Exclude handle spheres from element raycasting (they have their own list)
          if (child.userData['handleCorner']) {
            handleMeshes.push(child as InstanceType<typeof THREE.Mesh>);
          } else {
            elementMeshes.push(child as InstanceType<typeof THREE.Mesh>);
          }
        }
      });
    }
    raycaster.setElementMeshes(elementMeshes);
    raycaster.setHandleMeshes(handleMeshes);

    // Re-apply selection highlight after rebuild (so handles stay visible)
    const selectedIds = designState.getSelection();
    for (const selId of selectedIds) {
      const emg = elementMeshGroups.get(selId);
      if (emg) {
        elementMeshBuilder.setSelected(emg.group, true);
      }
    }

    sceneManager.markDirty();
  }

  /** Apply CSG boolean cuts to roof face meshes for dormers, chimneys, and skylights. */
  function applyElementCSGCuts(): void {
    const buildings = designState.getBuildings();
    for (const building of buildings) {
      if (!building.geometry) continue;
      const cuttable = building.elements.filter(
        e => e.type === 'dormer' || e.type === 'chimney' || e.type === 'skylight',
      );
      if (cuttable.length === 0) continue;

      for (const element of cuttable) {
        const faceId = element.faceId;
        if (!faceId) continue;

        const faceMesh = sceneManager.getFaceMesh(faceId);
        if (!faceMesh) continue;

        const face = building.geometry.faces.find(f => f.id === faceId);
        if (!face) continue;

        // Reuse the same face plane computation as the element mesh builder
        const plane = computeFacePlane(face);
        const faceNormal = plane.normal;
        const uAxis = plane.uAxis;
        const vAxis = plane.vAxis;

        // Project element centre onto face to get world position
        const centreWorld = new THREE.Vector3(
          element.positionPlan.x,
          0,
          element.positionPlan.z,
        );
        if (Math.abs(faceNormal.y) > 1e-6) {
          centreWorld.y = plane.origin.y
            - (faceNormal.x * (centreWorld.x - plane.origin.x) + faceNormal.z * (centreWorld.z - plane.origin.z)) / faceNormal.y;
        } else {
          centreWorld.y = plane.origin.y;
        }

        let cutMesh: import('three').Mesh | null = null;
        switch (element.type) {
          case 'dormer':
            cutMesh = csgOps.cutDormer(faceMesh, centreWorld, element.widthMm, element.depthMm, faceNormal, uAxis, vAxis);
            break;
          case 'chimney':
            cutMesh = csgOps.cutChimney(faceMesh, centreWorld, element.widthMm, element.depthMm);
            break;
          case 'skylight':
            cutMesh = csgOps.cutSkylight(faceMesh, centreWorld, element.widthMm, element.depthMm, faceNormal, uAxis, vAxis);
            break;
        }

        if (cutMesh) {
          sceneManager.replaceFaceMesh(faceId, cutMesh);
        }
      }
    }
  }

  // ── Plan overlay label system (DOM elements over the 2D plan pane) ────────
  // Face and element dimension labels are rendered as positioned DIVs over
  // the plan pane.  Their screen positions are computed by projecting world
  // coordinates through the orthographic plan camera.

  const planOverlay = document.createElement('div');
  planOverlay.className = 'plan-overlay-container';
  planPane.appendChild(planOverlay);

  /** DOM labels for face dimensions, keyed by faceId. */
  const facePlanLabels = new Map<string, HTMLDivElement>();
  /** DOM labels for element dimensions, keyed by elementId. */
  const elementPlanLabels = new Map<string, HTMLDivElement>();
  /** World positions (centroids) for face labels, keyed by faceId. */
  const faceLabelPositions = new Map<string, { x: number; y: number; z: number }>();
  /** World positions for element labels, keyed by elementId. */
  const elementLabelPositions = new Map<string, { x: number; y: number; z: number }>();

  /**
   * Project a world point to plan pane pixel coordinates.
   * Returns { x, y } relative to the plan pane top-left.
   */
  function worldToPlanPixel(wx: number, wy: number, wz: number): { x: number; y: number } {
    const cam = sceneManager.planView.camera;
    const v = new THREE.Vector3(wx, wy, wz).project(cam);
    const rect = planPane.getBoundingClientRect();
    return {
      x: (v.x + 1) / 2 * rect.width,
      y: (1 - v.y) / 2 * rect.height,
    };
  }

  /**
   * Rebuild plan overlay label DOM elements from current design state.
   * Called after geometry refresh or unit system change.
   */
  function rebuildPlanOverlayData(): void {
    // Clear existing
    for (const el of facePlanLabels.values()) el.remove();
    for (const el of elementPlanLabels.values()) el.remove();
    facePlanLabels.clear();
    elementPlanLabels.clear();
    faceLabelPositions.clear();
    elementLabelPositions.clear();

    const buildings = designState.getBuildings();
    const us = designState.getDesign()?.unitSystem ?? 'metric';

    for (const building of buildings) {
      if (!building.geometry) continue;

      // Face labels
      for (const face of building.geometry.faces) {
        let cx = 0, cy = 0, cz = 0;
        for (const v of face.vertices) { cx += v.x; cy += v.y; cz += v.z; }
        const n = face.vertices.length;
        cx /= n; cy /= n; cz /= n;

        const areaText = us === 'imperial'
          ? `${unitConv.sqMmToSqFt(face.actualArea).toFixed(0)} sq ft`
          : `${unitConv.sqMmToSqMetres(face.actualArea).toFixed(1)} m\u00b2`;
        const pitchText = face.pitch.risePer12 > 0 ? ` | ${face.pitch.risePer12.toFixed(1)}:12` : '';

        const div = document.createElement('div');
        div.className = 'face-dimension-label plan-overlay-label';
        div.textContent = `${areaText}${pitchText}`;
        div.style.display = 'none';
        planOverlay.appendChild(div);
        facePlanLabels.set(face.id, div);
        faceLabelPositions.set(face.id, { x: cx, y: cy, z: cz });
      }

      // Element labels
      for (const element of building.elements) {
        const div = document.createElement('div');
        div.className = 'element-dimension-label plan-overlay-label';
        const heightForLabel = element.type === 'chimney'
          ? (element.chimneyHeightAboveRoofMm ?? 900)
          : element.type === 'dormer'
            ? element.dormerWallHeightMm
            : undefined;
        div.textContent = ElementMeshBuilder.formatDimensions(element.widthMm, element.depthMm, us, heightForLabel);
        div.style.display = 'none';
        planOverlay.appendChild(div);
        elementPlanLabels.set(element.id, div);
        elementLabelPositions.set(element.id, {
          x: element.positionPlan.x,
          y: 0,
          z: element.positionPlan.z,
        });
      }
    }
  }

  /**
   * Show/hide and reposition plan overlay labels based on current selection.
   */
  function updatePlanOverlayLabels(): void {
    const selectedIds = designState.getSelection();
    const selectedSet = new Set(selectedIds);

    // Face labels
    for (const [faceId, div] of facePlanLabels) {
      if (selectedSet.has(faceId)) {
        div.style.display = '';
        const pos = faceLabelPositions.get(faceId);
        if (pos) {
          const px = worldToPlanPixel(pos.x, pos.y, pos.z);
          div.style.left = `${px.x}px`;
          div.style.top = `${px.y}px`;
        }
      } else {
        div.style.display = 'none';
      }
    }

    // Element labels
    for (const [elemId, div] of elementPlanLabels) {
      if (selectedSet.has(elemId)) {
        div.style.display = '';
        const pos = elementLabelPositions.get(elemId);
        if (pos) {
          const px = worldToPlanPixel(pos.x, pos.y, pos.z);
          div.style.left = `${px.x}px`;
          div.style.top = `${px.y}px`;
        }
      } else {
        div.style.display = 'none';
      }
    }
  }

  /**
   * Reposition all visible plan overlay labels (call on camera change / resize).
   */
  function repositionPlanOverlayLabels(): void {
    for (const [faceId, div] of facePlanLabels) {
      if (div.style.display === 'none') continue;
      const pos = faceLabelPositions.get(faceId);
      if (pos) {
        const px = worldToPlanPixel(pos.x, pos.y, pos.z);
        div.style.left = `${px.x}px`;
        div.style.top = `${px.y}px`;
      }
    }
    for (const [elemId, div] of elementPlanLabels) {
      if (div.style.display === 'none') continue;
      const pos = elementLabelPositions.get(elemId);
      if (pos) {
        const px = worldToPlanPixel(pos.x, pos.y, pos.z);
        div.style.left = `${px.x}px`;
        div.style.top = `${px.y}px`;
      }
    }
  }

  // Reposition labels when the plan camera moves (pan/zoom)
  sceneManager.planView.controls.addEventListener('change', repositionPlanOverlayLabels);

  // Reposition labels when the splitter is dragged
  sceneManager.splitter.onChange(() => {
    repositionPlanOverlayLabels();
  });

  function refreshSceneGeometry(): void {
    const buildings = designState.getBuildings();
    let hasGeometry = false;

    for (const building of buildings) {
      if (building.geometry) {
        sceneManager.setRoofGeometry(building.geometry);
        hasGeometry = true;
      }
    }

    if (!hasGeometry) {
      sceneManager.clearRoofGeometry();
    }

    // Rebuild element meshes (they depend on face geometry for slope projection)
    refreshElementMeshes();

    // Apply CSG cuts for dormers, chimneys, and skylights (must happen after fresh face meshes + elements)
    applyElementCSGCuts();

    // Update raycaster targets AFTER CSG cuts — CSG replaces face meshes,
    // so setting targets before would leave stale references to disposed geometry.
    raycaster.setFaceMeshes(sceneManager.getFaceMeshes());

    // Rebuild plan overlay labels after geometry refresh
    rebuildPlanOverlayData();
    updatePlanOverlayLabels();

    sceneManager.markDirty();
    updateUndoRedoState();
  }

  function fitCamerasToBuildings(): void {
    const buildings = designState.getBuildings();
    const box = new THREE.Box3();
    let hasPoints = false;

    for (const building of buildings) {
      if (!building.geometry) continue;
      for (const face of building.geometry.faces) {
        for (const v of face.vertices) {
          box.expandByPoint(new THREE.Vector3(v.x, v.y, v.z));
          hasPoints = true;
        }
      }
    }

    if (hasPoints) {
      sceneManager.fitCamerasToBox(box);
    }
  }

  // ── EventBus subscriptions ──────────────────────────────────────────────
  const unsubs: (() => void)[] = [];

  unsubs.push(eventBus.on('DESIGN_LOADED', () => {
    refreshSceneGeometry();
    fitCamerasToBuildings();
    buildingCounter = designState.getBuildings().length;
  }));

  unsubs.push(eventBus.on('BUILDING_ADDED', () => {
    refreshSceneGeometry();
    fitCamerasToBuildings();
  }));

  unsubs.push(eventBus.on('BUILDING_REMOVED', () => {
    refreshSceneGeometry();
  }));

  unsubs.push(eventBus.on('GEOMETRY_RECOMPUTED', () => {
    refreshSceneGeometry();
  }));

  unsubs.push(eventBus.on('ELEMENT_ADDED', () => {
    refreshSceneGeometry();
  }));

  unsubs.push(eventBus.on('ELEMENT_REMOVED', () => {
    refreshSceneGeometry();
  }));

  unsubs.push(eventBus.on('ELEMENT_MODIFIED', () => {
    refreshSceneGeometry();
  }));

  unsubs.push(eventBus.on('SAVE_STATE_CHANGED', () => {
    updateUndoRedoState();
  }));

  unsubs.push(eventBus.on('UNIT_SYSTEM_CHANGED', ({ unitSystem }) => {
    // Update status bar
    if (statusUnitsEl) statusUnitsEl.textContent = unitSystem === 'metric' ? 'Units: Metric' : 'Units: Imperial';
    if (statusGridEl) statusGridEl.textContent = unitSystem === 'metric' ? 'Grid: 1m' : "Grid: 1'";
    // Rebuild element meshes (geometry unchanged, but handles may need it)
    refreshElementMeshes();
    // Re-apply CSG cuts after element rebuild
    applyElementCSGCuts();
    raycaster.setFaceMeshes(sceneManager.getFaceMeshes());
    // Rebuild plan overlay labels with new units
    rebuildPlanOverlayData();
    updatePlanOverlayLabels();
    sceneManager.markDirty();
  }));

  // ── Load project data ──────────────────────────────────────────────────
  // Create a default design immediately so tools are functional while API loads
  designState.createNewDesign(projectId, 'metric');
  // Debug: expose designState on window for console verification
  (window as any).__designState = designState;
  const nameBtn = page.querySelector<HTMLButtonElement>('#project-name-btn');
  if (nameBtn) nameBtn.textContent = 'New Project';

  try {
    const [project, version] = await Promise.all([
      projectsApi.get(projectId),
      projectsApi.getLatestVersion(projectId).catch(() => null),
    ]);

    if (nameBtn) nameBtn.textContent = project.name;

    if (version?.designData) {
      designState.loadDesign(projectId, version.versionNumber, version.designData);
    }
  } catch {
    // API unavailable — default design already created above
  }

  // ── Pick up satellite-traced footprints ─────────────────────────────────
  // When arriving from the satellite tracing page, footprints are stored
  // in sessionStorage. Convert them into buildings if we have no existing data.
  const footprintKey = `roofer_satellite_footprints_${projectId}`;
  const storedFootprints = sessionStorage.getItem(footprintKey);
  if (storedFootprints && designState.getBuildings().length === 0) {
    try {
      const footprintSet = JSON.parse(storedFootprints) as
        import('@/satellite/FootprintConverter').ConvertedFootprintSet;

      for (let i = 0; i < footprintSet.footprints.length; i++) {
        const fp = footprintSet.footprints[i];
        buildingCounter++;
        const building: import('@/types').BuildingDesign = {
          id: crypto.randomUUID(),
          name: `Building ${buildingCounter}`,
          parameters: {
            type: 'gable',
            footprint: {
              vertices: fp.vertices,
              wallPlateHeight: 3000,
            },
            pitch: { risePer12: 6 },
            overhangEaveMm: 305,
            overhangRakeMm: 305,
            fasciaHeightMm: 150,
          },
          elements: [],
          materialSelection: {
            shingleType: 'three-tab',
            underlaymentType: 'felt-30',
            valleyType: 'closed-cut',
          },
        };
        commandStack.executeCommand(new AddFootprintCmd(building));
      }
      updateUndoRedoState();
    } catch {
      // Ignore corrupt satellite data
    }
    sessionStorage.removeItem(footprintKey);
  }

  // ── Cleanup on DOM removal ─────────────────────────────────────────────
  const observer = new MutationObserver(() => {
    if (!document.body.contains(page)) {
      observer.disconnect();
      document.removeEventListener('keydown', saveKeyHandler);
      commandStack.unbindKeyboardShortcuts();
      keyboardShortcuts.unbindEditorShortcuts();
      toolManager.dispose();
      propertiesPanel.destroy();
      csgOps.dispose();
      // Dispose element meshes
      for (const [, emg] of elementMeshGroups) {
        sceneManager.remove(emg.group);
        emg.dispose();
      }
      elementMeshGroups.clear();
      // Cleanup plan overlay
      sceneManager.planView.controls.removeEventListener('change', repositionPlanOverlayLabels);
      planOverlay.remove();
      facePlanLabels.clear();
      elementPlanLabels.clear();
      sceneManager.remove(previewGroup);
      sceneManager.dispose();
      unsubs.forEach(fn => fn());
      // Only unload the design if this is still the latest editor instance
      // (avoids wiping a design created by a newer page instance)
      if (instanceId === editorInstanceId) {
        designState.unloadDesign();
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}
